import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {
  Heart, MessageCircle, Share2, MoreHorizontal, Flag, Star, MapPin, Bookmark,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Avatar from './Avatar';
import TextExpandable from './TextExpandable';
import MediaCarousel from './MediaCarousel';
import CommentThread, { flattenCommentTree } from './CommentThread';
import { getDisplayName } from '../../utils/userDisplay';
import { toVideoPosterUrl } from '../../utils/mediaUrl';
import { richTextToPlainText } from '../../utils/richText';
import { classifyTouchAxis, TOUCH_GESTURE_THRESHOLD } from '../../utils/touchGesture';
import { calculateVisibleCategoryCount } from './mobileCategoryLayout';
import { trackRecommendationEvent } from '../../utils/recommendationEvents';
import './post.css';

const DOUBLE_TAP_DELAY = 280;
const MOBILE_COMMENTS_EXIT_DURATION = 190;
// iOS keeps animating the visual viewport for a few hundred milliseconds
// after the composer loses focus.  Unmounting the comments portal during that
// interval makes the nested snap feed re-measure itself and visibly moves the
// post (mobile-info and the three-dots action then appear to jump upwards).
const MOBILE_COMMENTS_CLOSE_DELAY = 420;

function formatDateRange(start, end) {
  if (!start) return '';
  const opts = { day: 'numeric', month: 'short', year: 'numeric' };
  try {
    if (end && end !== start) {
      return `${new Date(start).toLocaleDateString('pt-PT', opts)} – ${new Date(end).toLocaleDateString('pt-PT', opts)}`;
    }
    return new Date(start).toLocaleDateString('pt-PT', opts);
  } catch {
    return '';
  }
}

const POST_CURRENCY_SYMBOLS = {
  EUR: '€', USD: '$', GBP: '£', BRL: 'R$', CAD: 'C$', AUD: 'A$', JPY: '¥', CNY: '¥',
  INR: '₹', CHF: 'CHF ', MXN: 'MX$', ARS: '$', CLP: '$', COP: '$', PEN: 'S/ ',
  SEK: 'kr', NOK: 'kr', DKK: 'kr', PLN: 'zł', CZK: 'Kč', HUF: 'Ft', RON: 'lei ',
  TRY: '₺', ZAR: 'R', MAD: 'د.م. ', AED: 'د.إ ', NZD: 'NZ$', SGD: 'S$', THB: '฿',
  IDR: 'Rp ', KRW: '₩',
};
function formatPostPrice(value, currency = 'EUR') {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return '';
  const code = String(currency || 'EUR').toUpperCase();
  const symbol = POST_CURRENCY_SYMBOLS[code] || `${code} `;
  const formatted = new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 2 }).format(
    Math.round((amount + Number.EPSILON) * 100) / 100,
  );
  return `${symbol}${formatted}`;
}

const Post = ({
  travel,
  isLiked,
  isSaved,
  showAllComments,
  likedComments,
  comments = [],
  commentsLoading = false,
  currentUserId,
  user, // { id, name, profilePhoto, profilePicture }
  onLike,
  onShare,
  onLoadComments,
  onAddComment,
  onLikeComment,
  onDeleteComment,
  onOpenComments,
  onReport,
  onPhotoClick,
  onMediaClick,
  onToggleSave,
  imageEager = false,
  autoplayFeedVideosWithSound = false,
}) => {
  const navigate = useNavigate();
  const [burst, setBurst] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isCommentsClosing, setIsCommentsClosing] = useState(false);
  const [saved, setSaved] = useState(!!isSaved);
  const [composerValue, setComposerValue] = useState('');
  const [composerSubmitting, setComposerSubmitting] = useState(false);
  const [mobileVideoProgress, setMobileVideoProgress] = useState({ current: 0, duration: 0 });
  const [mobileActiveMediaIsVideo, setMobileActiveMediaIsVideo] = useState(false);
  const mobileVideoSeekRef = useRef(null);
  const postRef = useRef(null);
  const mobileChipsRef = useRef(null);
  const [visibleMobileCategoryCount, setVisibleMobileCategoryCount] = useState(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 767px)').matches
  ));

  useEffect(() => { setSaved(!!isSaved); }, [isSaved]);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleViewportChange = (event) => setIsMobileViewport(event.matches);
    setIsMobileViewport(mediaQuery.matches);
    mediaQuery.addEventListener?.('change', handleViewportChange);
    return () => mediaQuery.removeEventListener?.('change', handleViewportChange);
  }, []);

  // Count a trip as seen only after at least half of its card has remained on
  // screen for one second. This effect is intentionally scoped to Home, so
  // viewing cards on profile/travel/discovery pages does not consume the feed.
  useEffect(() => {
    const postElement = postRef.current;
    const tripId = travel?.id;
    if (currentUserId == null || tripId == null || !postElement?.closest('.gm-home__feed')) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const feed = postElement.closest('.gm-home__feed');
    let timer = null;
    let sent = false;
    const clearTimer = () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
    };
    const observer = new IntersectionObserver(([entry]) => {
      if (sent || !entry?.isIntersecting || entry.intersectionRatio < 0.5) {
        clearTimer();
        return;
      }
      if (timer === null) {
        timer = window.setTimeout(() => {
          timer = null;
          if (document.visibilityState === 'visible') {
            sent = true;
            trackRecommendationEvent({ eventType: 'IMPRESSION', tripId });
          }
        }, 1000);
      }
    }, {
      root: isMobileViewport ? feed : null,
      threshold: [0, 0.5],
    });
    observer.observe(postElement);
    return () => {
      clearTimer();
      observer.disconnect();
    };
  }, [currentUserId, isMobileViewport, travel?.id]);

  // React registers touchmove listeners as passive on modern browsers, so a
  // synthetic preventDefault cannot reliably stop native vertical scrolling
  // for a diagonal swipe. Use a non-passive native guard on Home posts: clear
  // vertical gestures remain native, while horizontal/diagonal gestures are
  // reserved for the media carousel before the feed can snap to another post.
  useEffect(() => {
    const postElement = postRef.current;
    if (!isMobileViewport || !postElement?.closest('.gm-home__feed')) return undefined;

    let touchOrigin = null;
    const onTouchStart = (event) => {
      const touch = event.touches?.[0] || event.changedTouches?.[0];
      touchOrigin = touch ? { x: touch.clientX, y: touch.clientY } : null;
    };
    const onTouchMove = (event) => {
      const touch = event.touches?.[0];
      if (!touchOrigin || !touch || showAllComments) return;
      const axis = classifyTouchAxis(
        touch.clientX - touchOrigin.x,
        touch.clientY - touchOrigin.y,
      );
      if (axis === 'horizontal' && event.cancelable) event.preventDefault();
    };
    const clearTouchOrigin = () => { touchOrigin = null; };

    postElement.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
    postElement.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
    postElement.addEventListener('touchend', clearTouchOrigin, true);
    postElement.addEventListener('touchcancel', clearTouchOrigin, true);
    return () => {
      postElement.removeEventListener('touchstart', onTouchStart, true);
      postElement.removeEventListener('touchmove', onTouchMove, true);
      postElement.removeEventListener('touchend', clearTouchOrigin, true);
      postElement.removeEventListener('touchcancel', clearTouchOrigin, true);
    };
  }, [isMobileViewport, showAllComments]);
  const lastTap = useRef(0);
  const tapTimer = useRef(null);
  const menuRef = useRef(null);
  const pendingCommentsCloseRef = useRef(null);
  const commentsSheetRef = useRef(null);
  const commentsBackdropRef = useRef(null);
  const commentsDragRef = useRef(null);
  const mobileOverlayGestureRef = useRef(null);
  const mobileOverlaySuppressClickRef = useRef(false);

  const ignoreMobileOverlayClick = useCallback((event) => {
    if (!mobileOverlaySuppressClickRef.current) return false;
    mobileOverlaySuppressClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
    return true;
  }, []);

  const handleMobileOverlayTouchStart = useCallback((event) => {
    if (showAllComments) return;
    const touch = event?.touches?.[0];
    if (!touch) return;
    mobileOverlayGestureRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      axis: null,
    };
    mobileOverlaySuppressClickRef.current = false;
  }, [showAllComments]);

  const handleMobileOverlayTouchMove = useCallback((event) => {
    if (showAllComments) return;
    const gesture = mobileOverlayGestureRef.current;
    const touch = event?.touches?.[0];
    if (!gesture || !touch) return;
    const deltaX = touch.clientX - gesture.startX;
    const deltaY = touch.clientY - gesture.startY;
    if (Math.hypot(deltaX, deltaY) >= TOUCH_GESTURE_THRESHOLD) {
      mobileOverlaySuppressClickRef.current = true;
    }
    if (!gesture.axis) gesture.axis = classifyTouchAxis(deltaX, deltaY);
    if (gesture.axis === 'horizontal' && event.cancelable) {
      // Prevent the browser from turning a drag that started on the author or
      // title button into a click/selection instead of a carousel swipe.
      event.preventDefault();
    }
  }, [showAllComments]);

  const handleMobileOverlayTouchEnd = useCallback((event) => {
    const gesture = mobileOverlayGestureRef.current;
    mobileOverlayGestureRef.current = null;
    if (!gesture || showAllComments) return;
    const touch = event?.changedTouches?.[0] || event?.touches?.[0];
    if (!touch) return;
    const deltaX = touch.clientX - gesture.startX;
    const deltaY = touch.clientY - gesture.startY;
    const axis = gesture.axis || classifyTouchAxis(deltaX, deltaY);
    if (axis !== 'horizontal' || Math.abs(deltaX) <= 38) return;
    const carousel = event.currentTarget?.closest('.gm-post')?.querySelector('.gm-post__media-frame .gm-carousel');
    if (!carousel) return;
    event.preventDefault();
    event.stopPropagation();
    mobileOverlaySuppressClickRef.current = true;
    carousel.dispatchEvent(new CustomEvent('gm-carousel-swipe', {
      detail: { deltaX },
    }));
  }, [showAllComments]);

  const cancelPendingCommentsClose = useCallback(() => {
    const pending = pendingCommentsCloseRef.current;
    if (!pending) return;
    if (pending.timer) window.clearTimeout(pending.timer);
    if (pending.frame) window.cancelAnimationFrame?.(pending.frame);
    pending.viewport?.removeEventListener('resize', pending.onViewportChange);
    pending.viewport?.removeEventListener('scroll', pending.onViewportChange);
    pendingCommentsCloseRef.current = null;
  }, []);

  useEffect(() => cancelPendingCommentsClose, [cancelPendingCommentsClose]);

  const toggleComments = useCallback(() => {
    if (!onOpenComments) return;

    // Opening is immediate. Desktop closing is immediate too because there
    // is no virtual keyboard resizing the feed behind the portal.
    if (!showAllComments || !isMobileViewport || typeof window === 'undefined') {
      cancelPendingCommentsClose();
      setIsCommentsClosing(false);
      onOpenComments(travel);
      return;
    }

    if (pendingCommentsCloseRef.current) return;

    // Only wait for iOS's longer keyboard settle when the keyboard was open.
    // A normal close still keeps the portal mounted for the short exit
    // animation, then releases the feed immediately.
    const keyboardOpen = document.documentElement.classList.contains('gm-home-keyboard-open')
      || document.body?.classList.contains('gm-home-keyboard-open');
    const focusedComposer = document.querySelector(
      '.gm-post__comments-wrap--mobile-portal .gm-ct__composer-input:focus',
    );
    const activeElement = document.activeElement;
    (focusedComposer || activeElement)?.blur?.();
    // Start the visual close right away. Keep the feed's scroll/viewport lock
    // until the keyboard has settled, but do not make the user wait for that
    // cleanup before the sheet begins moving out of view.
    setIsCommentsClosing(true);

    const finishClose = () => {
      const pending = pendingCommentsCloseRef.current;
      if (!pending) return;
      if (pending.timer) window.clearTimeout(pending.timer);
      pending.viewport?.removeEventListener('resize', pending.onViewportChange);
      pending.viewport?.removeEventListener('scroll', pending.onViewportChange);
      pendingCommentsCloseRef.current = null;
      setIsCommentsClosing(false);
      onOpenComments(travel);
    };

    const pending = {
      timer: null,
      frame: null,
    };
    const closeDelay = keyboardOpen || focusedComposer
      ? MOBILE_COMMENTS_CLOSE_DELAY
      : MOBILE_COMMENTS_EXIT_DURATION;
    pending.timer = window.setTimeout(finishClose, closeDelay);
    pendingCommentsCloseRef.current = pending;
  }, [
    cancelPendingCommentsClose,
    isMobileViewport,
    onOpenComments,
    showAllComments,
    travel,
  ]);

  // Let a downward touch anywhere on the mobile comments sheet dismiss it.
  // When the gesture starts in the scrollable comment list, wait until that
  // list reaches its top so ordinary reading/scrolling remains native.
  useEffect(() => {
    const sheet = commentsSheetRef.current;
    const backdrop = commentsBackdropRef.current;
    if (!showAllComments || !isMobileViewport || !sheet) return undefined;

    const clearDragVisuals = () => {
      sheet.classList.remove('gm-post__comments-wrap--dragging');
      sheet.style.removeProperty('--gm-home-comments-drag-y');
      backdrop?.classList.remove('gm-post__comments-backdrop--dragging');
      backdrop?.style.removeProperty('--gm-home-comments-backdrop-opacity');
    };

    const onTouchStart = (event) => {
      if (sheet.classList.contains('gm-post__comments-wrap--closing')) return;
      if (event.touches.length !== 1) {
        commentsDragRef.current = null;
        clearDragVisuals();
        return;
      }

      const touch = event.touches[0];
      const target = event.target;
      if (target?.closest?.('button, a, input, textarea, select, [contenteditable="true"], [role="button"]')) {
        commentsDragRef.current = null;
        return;
      }

      const list = target?.closest?.('.gm-ct__list') || null;
      commentsDragRef.current = {
        touchId: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        dragStartY: touch.clientY,
        lastY: touch.clientY,
        lastTime: event.timeStamp,
        velocityY: 0,
        startScrollTop: list ? list.scrollTop : 0,
        list,
        axis: null,
        dragging: false,
      };
    };

    const onTouchMove = (event) => {
      const gesture = commentsDragRef.current;
      if (!gesture || sheet.classList.contains('gm-post__comments-wrap--closing')) return;
      const touch = Array.from(event.touches).find((item) => item.identifier === gesture.touchId);
      if (!touch) return;

      const deltaX = touch.clientX - gesture.startX;
      const deltaY = touch.clientY - gesture.startY;
      if (!gesture.axis) gesture.axis = classifyTouchAxis(deltaX, deltaY);
      if (gesture.axis !== 'vertical' || deltaY <= 0) return;

      // Preserve normal native scrolling until the finger is pulling down
      // from the very top of the comment list.
      if (!gesture.dragging && gesture.list?.scrollTop > 0) return;

      if (!gesture.dragging) {
        gesture.dragging = true;
        gesture.dragStartY = gesture.startScrollTop > 0 ? touch.clientY : gesture.startY;
        sheet.classList.add('gm-post__comments-wrap--dragging');
        backdrop?.classList.add('gm-post__comments-backdrop--dragging');
      }

      if (event.cancelable) event.preventDefault();
      const dragY = Math.max(0, touch.clientY - gesture.dragStartY);
      sheet.style.setProperty('--gm-home-comments-drag-y', `${dragY}px`);
      const backdropOpacity = Math.max(0.42, 1 - (dragY / 520));
      backdrop?.style.setProperty('--gm-home-comments-backdrop-opacity', String(backdropOpacity));

      const elapsed = Math.max(1, event.timeStamp - gesture.lastTime);
      gesture.velocityY = (touch.clientY - gesture.lastY) / elapsed;
      gesture.lastY = touch.clientY;
      gesture.lastTime = event.timeStamp;
    };

    const onTouchEnd = (event) => {
      const gesture = commentsDragRef.current;
      if (!gesture) return;
      const touch = Array.from(event.changedTouches).find((item) => item.identifier === gesture.touchId);
      commentsDragRef.current = null;
      if (!gesture.dragging || !touch) {
        clearDragVisuals();
        return;
      }

      const dragY = Math.max(0, touch.clientY - gesture.dragStartY);
      const closeDistance = Math.max(90, sheet.clientHeight * 0.18);
      const shouldClose = dragY >= closeDistance || (dragY >= 44 && gesture.velocityY >= 0.7);
      if (shouldClose) {
        // Keep the last finger position in place. The regular closing class
        // then animates the sheet from there all the way off screen.
        sheet.classList.remove('gm-post__comments-wrap--dragging');
        backdrop?.classList.remove('gm-post__comments-backdrop--dragging');
        toggleComments();
        return;
      }

      clearDragVisuals();
    };

    const onTouchCancel = () => {
      commentsDragRef.current = null;
      clearDragVisuals();
    };

    sheet.addEventListener('touchstart', onTouchStart, { passive: true });
    sheet.addEventListener('touchmove', onTouchMove, { passive: false });
    sheet.addEventListener('touchend', onTouchEnd, { passive: true });
    sheet.addEventListener('touchcancel', onTouchCancel, { passive: true });
    return () => {
      sheet.removeEventListener('touchstart', onTouchStart);
      sheet.removeEventListener('touchmove', onTouchMove);
      sheet.removeEventListener('touchend', onTouchEnd);
      sheet.removeEventListener('touchcancel', onTouchCancel);
      commentsDragRef.current = null;
    };
  }, [isMobileViewport, showAllComments, toggleComments]);

  // Build the media list once per trip snapshot. Besides avoiding repeated
  // array work, this keeps MediaCarousel from restarting its preload effect
  // whenever a like, save or comment updates the Post state.
  const mediaUnique = useMemo(() => {
    const media = [];
      if (travel.highlightVideo) media.push({
        src: travel.highlightVideo,
        type: 'video',
        poster: travel.highlightVideoPoster || toVideoPosterUrl(travel.highlightVideo),
      });
    if (travel.highlightImage) media.push({ src: travel.highlightImage, type: 'photo' });
    if (Array.isArray(travel.images_generalInformation)) {
      travel.images_generalInformation.forEach((s) => s && media.push({ src: s, type: 'photo' }));
    }
    if (Array.isArray(travel.images_foodRecommendations)) {
      travel.images_foodRecommendations.forEach((s) => s && media.push({ src: s, type: 'photo' }));
    }
    if (Array.isArray(travel.images_transportMethods)) {
      travel.images_transportMethods.forEach((s) => s && media.push({ src: s, type: 'photo' }));
    }
    if (Array.isArray(travel.images_accommodations)) {
      travel.images_accommodations.forEach((s) => s && media.push({ src: s, type: 'photo' }));
    }
    if (Array.isArray(travel.images_referencePoints)) {
      travel.images_referencePoints.forEach((s) => s && media.push({ src: s, type: 'photo' }));
    }
    if (Array.isArray(travel.travelVideos)) {
        travel.travelVideos.forEach((s) => s && media.push({
          src: s,
          type: 'video',
          poster: toVideoPosterUrl(s),
        }));
    }
    const seen = new Set();
    return media.filter((m) => {
      if (!m.src || seen.has(m.src)) return false;
      seen.add(m.src);
      return true;
    });
  }, [
    travel.highlightImage,
      travel.highlightVideo,
    travel.highlightVideoPoster,
    travel.images_generalInformation,
    travel.images_foodRecommendations,
    travel.images_transportMethods,
    travel.images_accommodations,
    travel.images_referencePoints,
    travel.travelVideos,
  ]);

  const handleMobileVideoProgress = useCallback((current, duration, seek) => {
    mobileVideoSeekRef.current = typeof seek === 'function' ? seek : null;
    const nextCurrent = Number.isFinite(Number(current)) ? Math.max(0, Number(current)) : 0;
    const nextDuration = Number.isFinite(Number(duration)) ? Math.max(0, Number(duration)) : 0;
    setMobileVideoProgress((previous) => (
      previous.current === nextCurrent && previous.duration === nextDuration
        ? previous
        : { current: nextCurrent, duration: nextDuration }
    ));
  }, []);

  const handleActiveMediaChange = useCallback((isVideo) => {
    const nextIsVideo = Boolean(isVideo);
    setMobileActiveMediaIsVideo(nextIsVideo);
    if (!nextIsVideo) {
      mobileVideoSeekRef.current = null;
      setMobileVideoProgress((previous) => (
        previous.current === 0 && previous.duration === 0
          ? previous
          : { current: 0, duration: 0 }
      ));
    }
  }, []);

  useEffect(() => () => {
    mobileVideoSeekRef.current = null;
  }, []);

  const handleOpen = (e) => {
    if (e.target.closest('button, a, textarea, [role="button"]')) return;
    navigate(`/travel/${travel.id}`);
  };

  const triggerDoubleTapLike = useCallback(() => {
    if (!isLiked) onLike?.(travel.id);
    setBurst(true);
    window.setTimeout(() => setBurst(false), 900);
  }, [isLiked, onLike, travel.id]);

  // On the mobile Home feed a single photo tap opens the trip, while a
  // second tap inside the short window likes it. Desktop navigation stays
  // immediate.
  const handleMediaClick = useCallback((src, index) => {
    const isHomeMobile = typeof window !== 'undefined'
      && window.location.pathname === '/home'
      && window.matchMedia?.('(max-width: 767px)').matches;

    const openMediaTarget = () => {
      if (onMediaClick) onMediaClick(travel, src, index);
      else onPhotoClick?.(mediaUnique, index);
    };

    if (!isHomeMobile) {
      openMediaTarget();
      return;
    }

    const now = Date.now();
    if (lastTap.current && now - lastTap.current < DOUBLE_TAP_DELAY) {
      if (tapTimer.current) window.clearTimeout(tapTimer.current);
      tapTimer.current = null;
      lastTap.current = 0;
      triggerDoubleTapLike();
      return;
    }

    lastTap.current = now;
    if (tapTimer.current) window.clearTimeout(tapTimer.current);
    tapTimer.current = window.setTimeout(() => {
      lastTap.current = 0;
      tapTimer.current = null;
      openMediaTarget();
    }, DOUBLE_TAP_DELAY);
  }, [mediaUnique, onMediaClick, onPhotoClick, travel, triggerDoubleTapLike]);

  useEffect(() => () => {
    if (tapTimer.current) window.clearTimeout(tapTimer.current);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [menuOpen]);

  const likes = travel.likes || 0;
  const totalComments = Math.max(
    Number(travel.totalCommentsCount ?? 0),
    Array.isArray(comments) ? comments.length : 0,
  );
  // `author` is the display name (First Last) used in the avatar
  // and visible label. `authorUsername` is the raw @username used
  // for the /profile link so a profile rename doesn't break it.
  const author = travel.user || 'Viajante';
  const authorUsername = travel.userUsername || travel.user || '';
  // Hide the "save" affordance for the user's own posts — saving
  // a trip of yours doesn't make sense and was previously visible.
  const isOwnPost = currentUserId != null && travel.userId != null
    && String(currentUserId) === String(travel.userId);
  const title = travel.name || '';
  const summary = richTextToPlainText(travel.description || '');
  // V16 — Multi-destination rendering. For single-destination trips
  // the result is the familiar "Cidade, País" string. For multi-
  // destination, we render the full route in a compact form:
  //   1 dest   → "Lisboa, Portugal"
  //   2 dests  → "Lisboa, Portugal · Porto"
  //   3+ dests → "Lisboa, Portugal + 2" (full list in a tooltip)
  //   multi-country → flag emoji per country: "Lisboa 🇵🇹 + Paris 🇫🇷"
  // The `cities` and `countries` arrays come from the new V16 fields
  // on the feed DTO; we fall back to the legacy `city`/`country`
  // strings for trips that pre-date V16.
  const cities = Array.isArray(travel.cities) && travel.cities.length
    ? travel.cities
    : (travel.city ? [travel.city] : []);
  const countries = Array.isArray(travel.countries) && travel.countries.length
    ? travel.countries
    : (travel.country ? [travel.country] : []);
  const isMultiCountry = countries.length > 1;
  const FLAGS = { 'Portugal': '🇵🇹', 'Spain': '🇪🇸', 'France': '🇫🇷', 'Italy': '🇮🇹', 'Brazil': '🇧🇷', 'United States': '🇺🇸', 'United Kingdom': '🇬🇧', 'Germany': '🇩🇪', 'Netherlands': '🇳🇱', 'Japan': '🇯🇵', 'China': '🇨🇳', 'Mexico': '🇲🇽' };
  const buildLocation = () => {
    if (cities.length === 0) return '';
    const first = cities[0];
    const firstCountry = countries[0] || '';
    const head = isMultiCountry
      ? `${first}${FLAGS[firstCountry] ? ' ' + FLAGS[firstCountry] : ''}`
      : (firstCountry ? `${first}, ${firstCountry}` : first);
    if (cities.length === 1) return head;
    if (cities.length === 2) {
      return isMultiCountry
        ? `${head} + ${cities[1]}${FLAGS[countries[1] || ''] ? ' ' + FLAGS[countries[1]] : ''}`
        : `${head} · ${cities[1]}`;
    }
    // 3+ → head + (N-1) com tooltip a listar tudo
    return `${head} + ${cities.length - 1}`;
  };
  const location = buildLocation();
  const locationTooltip = cities.length > 2
    ? `Roteiro: ${cities.join(' → ')}`
    : (isMultiCountry ? `Roteiro: ${cities.map((c, i) => `${c}${FLAGS[countries[i] || ''] ? ' ' + FLAGS[countries[i]] : ''}`).join(' → ')}` : '');
  const dateRange = formatDateRange(travel.startDate, travel.endDate);
  const price = travel.price;
  const stars = travel.stars;
  // Keep all category labels from the backend; mobile measures how many fit
  // on one line and replaces overflow with a compact +N chip.
  const categories = Array.isArray(travel.category)
    ? travel.category.filter(Boolean)
    : (travel.category ? [travel.category] : []);
  const categoryKey = categories.join('\u0000');

  useLayoutEffect(() => {
    const container = mobileChipsRef.current;
    const measureRow = container?.querySelector('.gm-post__mobile-chips-measure');
    if (!container || !measureRow) return undefined;
    const categoryCount = categories.length;

    const measure = () => {
      const widths = Array.from(measureRow.children)
        .slice(0, categoryCount)
        .map((chip) => chip.getBoundingClientRect().width);
      const overflowWidth = measureRow.children[categoryCount]?.getBoundingClientRect().width || 0;
      const gap = Number.parseFloat(window.getComputedStyle(measureRow).columnGap) || 0;
      const availableWidth = container.getBoundingClientRect().width || container.clientWidth;
      setVisibleMobileCategoryCount(calculateVisibleCategoryCount(
        widths,
        overflowWidth,
        gap,
        availableWidth,
      ));
    };

    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(measureRow);
    return () => observer.disconnect();
  }, [categoryKey]);

  // Flatten the comment tree once for the global thread component.
  const flatComments = useMemo(() => {
    const flattened = flattenCommentTree(comments);
    // Sync the liked state from the `likedComments` array into the
    // comment objects so the heart icon renders filled.
    if (Array.isArray(likedComments) && likedComments.length) {
      const likedSet = new Set(likedComments);
      return flattened.map((c) => ({
        ...c,
        currentUserLiked: likedSet.has(c.id) || likedSet.has(`${travel.id}-${c.id}`) || c.currentUserLiked,
      }));
    }
    return flattened;
  }, [comments, likedComments, travel.id]);

  const handleComposerSubmit = useCallback(async (text) => {
    if (!text?.trim() || composerSubmitting) return false;
    setComposerSubmitting(true);
    try {
      const result = await onAddComment?.(travel.id, [], text);
      if (result !== false) setComposerValue('');
      return result !== false;
    } finally {
      setComposerSubmitting(false);
    }
  }, [composerSubmitting, onAddComment, travel.id]);

  const handleReplySubmit = useCallback((comment, text) => {
    if (comment?.id == null) return false;
    return onAddComment?.(travel.id, [comment.id], text);
  }, [onAddComment, travel.id]);

  const handleCommentLike = useCallback((comment) => {
    if (!comment) return;
    onLikeComment?.(travel.id, comment.id, []);
  }, [onLikeComment, travel.id]);

  const handleCommentDelete = useCallback((comment) => {
    if (!comment) return;
    onDeleteComment?.(travel.id, comment.id);
  }, [onDeleteComment, travel.id]);

  // The Home comments sheet must live at document level on every viewport.
  // The post is animated by Framer Motion and can therefore create a
  // transformed containing block, which makes `position: fixed` behave like
  // an element inside the feed and get clipped or positioned incorrectly.
  const commentsPanel = (
    <>
      {showAllComments && (
        <button
          ref={commentsBackdropRef}
          type="button"
          className={`gm-post__comments-backdrop ${!isMobileViewport ? 'gm-post__comments-backdrop--desktop' : ''} ${isCommentsClosing ? 'gm-post__comments-backdrop--closing' : ''}`.trim()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleComments();
          }}
          aria-label="Fechar comentários"
        />
      )}
      <div
        ref={commentsSheetRef}
        className={`gm-post__comments-wrap ${showAllComments ? 'gm-post__comments-wrap--open gm-post__comments-wrap--home-sheet' : 'gm-post__comments-wrap--closed'} ${isMobileViewport && showAllComments ? 'gm-post__comments-wrap--mobile-portal' : ''} ${isCommentsClosing ? 'gm-post__comments-wrap--closing' : ''}`.trim()}
        onClick={(e) => e.stopPropagation()}
        role={showAllComments ? 'dialog' : undefined}
        aria-modal={showAllComments ? 'true' : undefined}
        aria-label={showAllComments ? 'Comentários da viagem' : undefined}
      >
        <CommentThread
          isOpen={!!showAllComments}
          mobileSheet={showAllComments}
          onToggle={toggleComments}
          comments={flatComments}
          totalCount={totalComments}
          currentUserId={currentUserId}
          onLike={handleCommentLike}
          onDelete={handleCommentDelete}
          onReply={handleReplySubmit}
          onUserClick={(comment) => {
            const username = comment?.username;
            const userId = comment?.userId;
            if (username) navigate(`/profile/${username}`);
            else if (userId != null) navigate(`/profile/${userId}`);
          }}
          composer={{
            author: {
              name: getDisplayName(user, 'Você'),
              src: user?.profilePhoto || user?.profilePicture,
              photoPosition: user?.profilePhotoPosition,
            },
            value: composerValue,
            onChange: setComposerValue,
            onSubmit: handleComposerSubmit,
            disabled: composerSubmitting,
            placeholder: 'Escreva um comentário...',
          }}
           loading={commentsLoading}
        />
      </div>
    </>
  );

  return (
    <motion.article
      ref={postRef}
      className={`gm-post ${showAllComments ? 'gm-post--comments-open' : ''}`}
      aria-label={`Publicação de ${author}`}
      // Do not animate the feed item that contains the LCP image. The
      // previous 320ms entrance animation delayed its first usable paint;
      // menus and other intentional interactions keep their own animations.
      initial={false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      onClick={handleOpen}
    >
      {/* ── Header: avatar + author + location + date + more ── */}
      <header className="gm-post__header">
        <button
          type="button"
          className="gm-post__avatar-btn"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/profile/${authorUsername}`); }}
          aria-label={`Ver perfil de ${author}`}
        >
          <Avatar
            src={travel.userProfilePicture}
            name={author}
            size="md"
            photoPosition={travel.userProfilePhotoPosition}
          />
        </button>
        <div className="gm-post__author">
          <button
            type="button"
            className="gm-post__author-name"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/profile/${authorUsername}`); }}
            aria-label={`Ver perfil de ${author}`}
          >
            {author}
          </button>
          {(location || dateRange) && (
            <span className="gm-post__author-meta">
              {location && (
                <span className="gm-post__author-meta-row" title={locationTooltip || undefined}>
                  <MapPin size={11} strokeWidth={2} className="gm-post__author-pin" />
                  {location}
                </span>
              )}
              {dateRange && (
                <span className="gm-post__author-meta-row gm-post__author-meta-row--muted">
                  {dateRange}
                </span>
              )}
            </span>
          )}
        </div>
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            className="gm-post__more"
            aria-label="Mais opções"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen((o) => !o); }}
          >
            <MoreHorizontal size={20} strokeWidth={1.75} />
          </button>
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                className="gm-post__menu"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.16 }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Round 58 — Denunciar a própria publicação não faz
                    sentido. O menu agora mostra o item apenas para
                    posts de outros utilizadores, alinhando com a
                    regra que já existia no TravelDetails. */}
                {!isOwnPost && (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(false); onReport?.(travel); }}
                  >
                    <Flag size={14} strokeWidth={1.75} />
                    Denunciar publicação
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* ── Title (optional) ─────────────────────────────── */}
      {title && <h2 className="gm-post__title">{title}</h2>}

      {/* ── Media carousel (huge photo, 3:4) ─────────────── */}
      {mediaUnique.length > 0 && (
        <div className="gm-post__media">
          <div className="gm-post__media-frame">
            <MediaCarousel
              media={mediaUnique}
              eager={imageEager}
              showMobileVideoControls={isMobileViewport}
              autoplayWithSound={autoplayFeedVideosWithSound}
              onVideoProgress={isMobileViewport ? handleMobileVideoProgress : undefined}
              onActiveMediaChange={isMobileViewport ? handleActiveMediaChange : undefined}
              onPhotoClick={handleMediaClick}
              onVideoDoubleTap={() => triggerDoubleTapLike()}
              soundButtonClassName="gm-video-sound-button--feed"
            />
            {/* Meta overlay (categories + stars + price) */}
            {(categories.length > 0 || (price && Number(price) > 0) || stars > 0 || travel.isMultiDest || Number(travel.citiesCount) === 1) && (
              <div className="gm-post__media-meta">
                {(categories.length > 0 || travel.isMultiDest || Number(travel.citiesCount) === 1) && (
                  <div className="gm-post__media-categories">
                    {categories.map((cat) => (
                      <span key={cat} className="gm-post__media-pill">{cat}</span>
                    ))}
                    {/* Round 49 — feed parity with /travels: "Multidestino"
                        (blue) when the trip has more than one city, or
                        "Destino Único" (slate) when it has exactly one.
                        Mirrors Travels.js pill naming. */}
                    {travel.isMultiDest ? (
                      <span
                        className="gm-post__media-pill gm-post__media-pill--multi"
                        title="Viagem com vários destinos"
                      >
                        <MapPin size={11} strokeWidth={2.2} /> Multidestino
                      </span>
                    ) : (Number(travel.citiesCount) === 1 && (
                      <span
                        className="gm-post__media-pill gm-post__media-pill--single"
                        title="Viagem com um único destino"
                      >
                        <MapPin size={11} strokeWidth={2.2} /> Destino Único
                      </span>
                    ))}
                  </div>
                )}
                {(stars > 0 || (price && Number(price) > 0)) && (
                  <div className="gm-post__media-meta-side" aria-label="Avaliação e preço">
                    {stars > 0 && (
                      <span className="gm-post__media-pill gm-post__media-pill--stars">
                        <Star size={11} strokeWidth={0} fill="currentColor" />
                        {Number(stars).toFixed(1)}
                      </span>
                    )}
                    {price && Number(price) > 0 && (
                      <span className="gm-post__media-pill gm-post__media-pill--price">{formatPostPrice(price, travel.currency)}</span>
                    )}
                  </div>
                )}
              </div>
            )}
            <AnimatePresence>
              {burst && (
                <div className="gm-post__heart-burst" aria-hidden="true">
                  <Heart size={120} strokeWidth={0} fill="currentColor" />
                </div>
              )}
            </AnimatePresence>

            {/* Mobile-only TikTok/Snap presentation. The normal post
                controls remain in the DOM for desktop and accessibility;
                this overlay reuses the same callbacks without changing the
                desktop layout. */}
            <div
              className={`gm-post__mobile-overlay ${showAllComments ? 'gm-post__mobile-overlay--comments-open' : ''}`}
              onTouchStartCapture={handleMobileOverlayTouchStart}
              onTouchMoveCapture={handleMobileOverlayTouchMove}
              onTouchEndCapture={handleMobileOverlayTouchEnd}
              onTouchCancelCapture={() => { mobileOverlayGestureRef.current = null; }}
              onClickCapture={(event) => { ignoreMobileOverlayClick(event); }}
              onClick={(e) => {
                // When the comments sheet is open, any empty area of the
                // post is a close target. Stop the post click from opening
                // the trip details page. Buttons keep their own action.
                if (!showAllComments) return;
                if (e.target.closest('button, a, textarea, input, [role="button"]')) return;
                e.preventDefault();
                e.stopPropagation();
                toggleComments();
              }}
            >
              <div className="gm-post__mobile-gradient" aria-hidden="true" />

              <div className="gm-post__mobile-actions" aria-label="Ações da viagem">
                <button
                  type="button"
                  className={`gm-post__mobile-action ${isLiked ? 'gm-post__mobile-action--liked' : ''}`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onLike?.(travel.id, e); }}
                  aria-label={isLiked ? 'Remover gosto' : 'Dar gosto'}
                  aria-pressed={isLiked}
                >
                  <Heart size={29} strokeWidth={1.8} fill={isLiked ? 'currentColor' : 'none'} />
                  <span>{likes}</span>
                </button>

                <button
                  type="button"
                  className={`gm-post__mobile-action ${showAllComments ? 'gm-post__mobile-action--active' : ''}`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleComments(); }}
                  aria-label={showAllComments ? 'Esconder comentários' : 'Ver comentários'}
                  aria-pressed={showAllComments}
                >
                  <MessageCircle size={29} strokeWidth={1.8} />
                  <span>{totalComments}</span>
                </button>

                <button
                  type="button"
                  className="gm-post__mobile-action"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShare?.(travel.id, e); }}
                  aria-label="Partilhar"
                >
                  <Share2 size={28} strokeWidth={1.8} />
                  <span>Partilhar</span>
                </button>

                {!isOwnPost && (
                  <button
                    type="button"
                    className={`gm-post__mobile-action ${saved ? 'gm-post__mobile-action--saved' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const next = !saved;
                      setSaved(next);
                      onToggleSave?.(travel.id, next);
                    }}
                    aria-label={saved ? 'Remover dos guardados' : 'Guardar'}
                    aria-pressed={saved}
                  >
                    <Bookmark size={28} strokeWidth={1.8} fill={saved ? 'currentColor' : 'none'} />
                    <span>Guardar</span>
                  </button>
                )}
              </div>

              <div
                className="gm-post__mobile-info"
              >
                <button
                  type="button"
                  className="gm-post__mobile-author"
                  onClick={(e) => {
                    if (ignoreMobileOverlayClick(e)) return;
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/profile/${authorUsername}`);
                  }}
                  aria-label={`Ver perfil de ${author}`}
                >
                  <Avatar
                    src={travel.userProfilePicture}
                    name={author}
                    size="sm"
                    photoPosition={travel.userProfilePhotoPosition}
                  />
                  <span>{author}</span>
                </button>

                {location && (
                  <span className="gm-post__mobile-location">
                    <MapPin size={15} strokeWidth={2.2} />
                    {location}
                  </span>
                )}

                {title && (
                  <button
                  type="button"
                  className="gm-post__mobile-title"
                    onClick={(e) => {
                      if (ignoreMobileOverlayClick(e)) return;
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/travel/${travel.id}`);
                    }}
                  >
                    {title}
                  </button>
                )}

                {summary && <p className="gm-post__mobile-summary">{summary}</p>}

                {categories.length > 0 && (
                  <div ref={mobileChipsRef} className="gm-post__mobile-chips" aria-label="Categorias da viagem">
                    {categories.slice(0, Math.min(
                      categories.length,
                      visibleMobileCategoryCount ?? categories.length,
                    )).map((cat) => <span key={cat}>{cat}</span>)}
                    {categories.length > (visibleMobileCategoryCount ?? categories.length) && (
                      <span
                        className="gm-post__mobile-chips-overflow"
                        title={categories.slice(visibleMobileCategoryCount).join(', ')}
                      >
                        +{categories.length - visibleMobileCategoryCount}
                      </span>
                    )}
                    <div className="gm-post__mobile-chips-measure" aria-hidden="true">
                      {categories.map((cat) => <span key={cat}>{cat}</span>)}
                      <span>+{categories.length}</span>
                    </div>
                  </div>
                )}

                {isMobileViewport && mobileActiveMediaIsVideo && (
                  <div className="gm-post__mobile-progress" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="range"
                      min="0"
                      max={mobileVideoProgress.duration > 0 ? mobileVideoProgress.duration : 1}
                      step="0.01"
                      value={mobileVideoProgress.duration > 0
                        ? Math.min(mobileVideoProgress.current, mobileVideoProgress.duration)
                        : 0}
                      style={{ '--gm-post-video-progress': `${mobileVideoProgress.duration > 0
                        ? (Math.min(mobileVideoProgress.current, mobileVideoProgress.duration) / mobileVideoProgress.duration) * 100
                        : 0}%` }}
                      aria-label="Progresso do vídeo"
                      onPointerDown={(e) => e.stopPropagation()}
                      onPointerMove={(e) => e.stopPropagation()}
                      onPointerUp={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      onTouchMove={(e) => { e.stopPropagation(); e.preventDefault(); }}
                      onTouchEnd={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onInput={(e) => mobileVideoSeekRef.current?.(Number(e.currentTarget.value))}
                      onChange={(e) => mobileVideoSeekRef.current?.(Number(e.currentTarget.value))}
                    />
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Mobile-only category strip: under the photo instead of overlaid.
          Hides on desktop where the overlay is shown. Static, clean
          wrap so it matches the rest of the design system. The
          Multidestino / Destino Único pill rides along even when
          there are no categories. */}
      {(categories.length > 0 || travel.isMultiDest || Number(travel.citiesCount) === 1) && (
        <div className="gm-post__categories-mobile" aria-label="Categorias e tipo de destino">
          {categories.map((cat) => (
            <span key={cat} className="gm-post__media-pill">{cat}</span>
          ))}
          {travel.isMultiDest ? (
            <span
              className="gm-post__media-pill gm-post__media-pill--multi"
              title="Viagem com vários destinos"
            >
              <MapPin size={11} strokeWidth={2.2} /> Multidestino
            </span>
          ) : (Number(travel.citiesCount) === 1 && (
            <span
              className="gm-post__media-pill gm-post__media-pill--single"
              title="Viagem com um único destino"
            >
              <MapPin size={11} strokeWidth={2.2} /> Destino Único
            </span>
          ))}
        </div>
      )}

      {/* ── Caption (description) — click opens travel ── */}
      {summary && (
        <div
          className="gm-post__caption gm-post__caption--clickable"
          onClick={(e) => { e.stopPropagation(); navigate(`/travel/${travel.id}`); }}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/travel/${travel.id}`); } }}
        >
          <TextExpandable text={summary} clamp={6} />
        </div>
      )}

      {/* ── Actions ─────────────────────────────────────── */}
      <div className="gm-post__actions" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={`gm-post__action ${isLiked ? 'gm-post__action--liked' : ''}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onLike?.(travel.id, e); }}
          aria-label={isLiked ? 'Remover gosto' : 'Dar gosto'}
          aria-pressed={isLiked}
        >
          <Heart
            size={20}
            strokeWidth={1.75}
            fill={isLiked ? 'currentColor' : 'none'}
          />
          {likes > 0 && <span className="gm-post__action-count">{likes}</span>}
        </button>
        <button
          type="button"
          className={`gm-post__action ${showAllComments ? 'gm-post__action--active' : ''}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleComments(); }}
          aria-label={showAllComments ? 'Esconder comentários' : 'Ver comentários'}
          aria-pressed={showAllComments}
        >
          <MessageCircle size={20} strokeWidth={1.75} />
          {totalComments > 0 && <span className="gm-post__action-count">{totalComments}</span>}
        </button>
        <button
          type="button"
          className="gm-post__action"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShare?.(travel.id, e); }}
          aria-label="Partilhar"
        >
          <Share2 size={20} strokeWidth={1.75} />
        </button>
        <div className="gm-post__action-spacer" />
        {!isOwnPost && (
          <button
            type="button"
            className={`gm-post__action ${saved ? 'gm-post__action--saved' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const next = !saved;
              setSaved(next);
              onToggleSave?.(travel.id, next);
            }}
            aria-label={saved ? 'Remover dos guardados' : 'Guardar'}
            aria-pressed={saved}
          >
            <Bookmark size={20} strokeWidth={1.75} fill={saved ? 'currentColor' : 'none'} />
          </button>
        )}
      </div>

      {/* ── Comments (global thread) ─────────────────────── */}
      {showAllComments && typeof document !== 'undefined'
        ? createPortal(commentsPanel, document.body)
        : commentsPanel}
    </motion.article>
  );
};

Post.propTypes = {
  travel: PropTypes.object.isRequired,
  isLiked: PropTypes.bool,
  isSaved: PropTypes.bool,
  showAllComments: PropTypes.bool,
  likedComments: PropTypes.array,
  comments: PropTypes.array,
  commentsLoading: PropTypes.bool,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  user: PropTypes.object,
  onLike: PropTypes.func,
  onShare: PropTypes.func,
  onLoadComments: PropTypes.func,
  onAddComment: PropTypes.func,
  onLikeComment: PropTypes.func,
  onDeleteComment: PropTypes.func,
  onOpenComments: PropTypes.func,
  onReport: PropTypes.func,
  onPhotoClick: PropTypes.func,
  onMediaClick: PropTypes.func,
  onToggleSave: PropTypes.func,
  imageEager: PropTypes.bool,
};

export default Post;
